import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Package, Plus, Edit2, Trash2, Check, X, RefreshCw, 
  Zap, Star, Crown, Shield, Rocket, Gem, Award, Building2,
  Users, Video, FileText, Mail, Bell, Calendar, BarChart3,
  Lock, Cloud, Cpu, Headphones, CreditCard, ExternalLink,
  ToggleLeft, ToggleRight, AlertCircle, ChevronDown
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const ICON_COMPONENTS = {
  package: Package,
  zap: Zap,
  star: Star,
  crown: Crown,
  shield: Shield,
  rocket: Rocket,
  gem: Gem,
  award: Award,
  building: Building2,
  users: Users,
  video: Video,
  'file-text': FileText,
  mail: Mail,
  bell: Bell,
  calendar: Calendar,
  chart: BarChart3,
  lock: Lock,
  cloud: Cloud,
  cpu: Cpu,
  headphones: Headphones,
};

const AdminProduits = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [icons, setIcons] = useState([]);
  const [billingPeriods, setBillingPeriods] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'EUR',
    billing_period: 'monthly',
    duration_days: null,
    icon: 'package',
    features: [''],
    is_highlighted: false,
    highlight_text: '',
    max_users: null,
    max_buildings: null,
    active: true,
    sort_order: 0,
    sync_stripe: true
  });
  
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsRes, iconsRes, periodsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/produits`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/produits/icons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/produits/billing-periods`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/produits/stats/overview`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      setProducts(productsRes.data.products || []);
      setIcons(iconsRes.data.icons || []);
      setBillingPeriods(periodsRes.data.periods || []);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      // Clean features array
      const cleanedFeatures = formData.features.filter(f => f.trim() !== '');
      
      const res = await axios.post(`${API}/admin/produits`, {
        ...formData,
        features: cleanedFeatures,
        price: parseFloat(formData.price)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(res.data.stripe_synced 
        ? 'Produit créé et synchronisé avec Stripe ✓' 
        : 'Produit créé avec succès');
      setShowCreateModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const cleanedFeatures = formData.features.filter(f => f.trim() !== '');
      
      await axios.put(`${API}/admin/produits/${selectedProduct.id}`, {
        ...formData,
        features: cleanedFeatures,
        price: parseFloat(formData.price)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Produit mis à jour avec succès');
      setShowEditModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit ? Cette action est irréversible.')) return;
    
    try {
      await axios.delete(`${API}/admin/produits/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Produit supprimé');
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleSyncStripe = async (productId) => {
    setSyncing(productId);
    try {
      await axios.post(`${API}/admin/produits/${productId}/sync-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Produit synchronisé avec Stripe');
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de synchronisation Stripe');
    } finally {
      setSyncing(null);
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await axios.put(`${API}/admin/produits/${product.id}`, {
        active: !product.active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const openCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      currency: 'EUR',
      billing_period: 'monthly',
      duration_days: null,
      icon: 'package',
      features: [''],
      is_highlighted: false,
      highlight_text: '',
      max_users: null,
      max_buildings: null,
      active: true,
      sort_order: products.length,
      sync_stripe: true
    });
    setError(null);
    setShowCreateModal(true);
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price || 0,
      currency: product.currency || 'EUR',
      billing_period: product.billing_period || 'monthly',
      duration_days: product.duration_days || null,
      icon: product.icon || 'package',
      features: product.features?.length > 0 ? product.features : [''],
      is_highlighted: product.is_highlighted || false,
      highlight_text: product.highlight_text || '',
      max_users: product.max_users || null,
      max_buildings: product.max_buildings || null,
      active: product.active !== false,
      sort_order: product.sort_order || 0,
      sync_stripe: false
    });
    setError(null);
    setShowEditModal(true);
  };

  const addFeature = () => {
    setFormData({...formData, features: [...formData.features, '']});
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({...formData, features: newFeatures});
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({...formData, features: newFeatures.length > 0 ? newFeatures : ['']});
  };

  const formatPrice = (price, currency, billingPeriod) => {
    const periodLabels = {
      one_time: '',
      monthly: '/mois',
      quarterly: '/trim.',
      yearly: '/an'
    };
    return `${price?.toFixed(2) || '0.00'} ${currency}${periodLabels[billingPeriod] || ''}`;
  };

  const getBillingLabel = (period) => {
    const labels = {
      one_time: 'Paiement unique',
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      yearly: 'Annuel'
    };
    return labels[period] || period;
  };

  const renderIcon = (iconId, size = 24) => {
    const IconComponent = ICON_COMPONENTS[iconId] || Package;
    return <IconComponent size={size} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#00D3A8] mr-2" size={24} />
        <span>Chargement...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Produits</h1>
          <p className="text-gray-500 mt-1">Créez et gérez vos offres commerciales</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors"
          data-testid="create-product-btn"
        >
          <Plus size={20} />
          <span>Nouveau produit</span>
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Check size={20} />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_products || 0}</p>
                <p className="text-sm text-gray-500">Produits</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active_products || 0}</p>
                <p className="text-sm text-gray-500">Actifs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.stripe_synced || 0}</p>
                <p className="text-sm text-gray-500">Sur Stripe</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Users className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_subscriptions || 0}</p>
                <p className="text-sm text-gray-500">Abonnements</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Zap className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active_subscriptions || 0}</p>
                <p className="text-sm text-gray-500">Actifs</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(product => (
          <div 
            key={product.id} 
            className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${
              product.is_highlighted 
                ? 'border-[#00D3A8] shadow-lg shadow-[#00D3A8]/20' 
                : 'border-gray-100 hover:border-gray-200'
            } ${!product.active ? 'opacity-60' : ''}`}
          >
            {/* Highlight Badge */}
            {product.is_highlighted && (
              <div className="bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white text-center py-1.5 text-sm font-medium">
                {product.highlight_text || '⭐ Recommandé'}
              </div>
            )}
            
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-xl ${product.is_highlighted ? 'bg-[#00D3A8]/10 text-[#00D3A8]' : 'bg-gray-100 text-gray-600'}`}>
                    {renderIcon(product.icon, 28)}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{product.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      product.billing_period === 'one_time' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {getBillingLabel(product.billing_period)}
                    </span>
                  </div>
                </div>
                
                {/* Status */}
                <button
                  onClick={() => handleToggleActive(product)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    product.active 
                      ? 'text-green-600 hover:bg-green-50' 
                      : 'text-gray-400 hover:bg-gray-50'
                  }`}
                  title={product.active ? 'Actif - Cliquer pour désactiver' : 'Inactif - Cliquer pour activer'}
                >
                  {product.active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                </button>
              </div>
              
              {/* Price */}
              <div className="mb-4">
                <span className="text-3xl font-bold text-gray-900">{product.price?.toFixed(2)}</span>
                <span className="text-gray-500 ml-1">{product.currency}</span>
                {product.billing_period !== 'one_time' && (
                  <span className="text-gray-400 text-sm">/{product.billing_period === 'monthly' ? 'mois' : product.billing_period === 'yearly' ? 'an' : 'trim.'}</span>
                )}
              </div>
              
              {/* Description */}
              {product.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
              )}
              
              {/* Features */}
              {product.features?.length > 0 && (
                <ul className="space-y-2 mb-4">
                  {product.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <Check className="text-[#00D3A8] mr-2 flex-shrink-0" size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                  {product.features.length > 4 && (
                    <li className="text-sm text-gray-400">+{product.features.length - 4} autres...</li>
                  )}
                </ul>
              )}
              
              {/* Stripe Status */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  {product.stripe_product_id ? (
                    <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      <CreditCard size={12} className="mr-1" />
                      Stripe ✓
                    </span>
                  ) : (
                    <span className="flex items-center text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                      <CreditCard size={12} className="mr-1" />
                      Non sync
                    </span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-1">
                  {!product.stripe_product_id && (
                    <button
                      onClick={() => handleSyncStripe(product.id)}
                      disabled={syncing === product.id}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Synchroniser avec Stripe"
                    >
                      {syncing === product.id ? (
                        <RefreshCw size={18} className="animate-spin" />
                      ) : (
                        <ExternalLink size={18} />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => openEditModal(product)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteProduct(product.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {products.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
            <Package className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">Aucun produit créé</p>
            <button
              onClick={openCreateModal}
              className="mt-4 text-[#00D3A8] hover:underline"
            >
              Créer votre premier produit
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {showCreateModal ? 'Nouveau produit' : 'Modifier le produit'}
                </h2>
                <button 
                  onClick={() => { setShowCreateModal(false); setShowEditModal(false); }} 
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <form onSubmit={showCreateModal ? handleCreateProduct : handleUpdateProduct} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Pack Premium, Module AG..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Description du produit..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icône du produit</label>
                <div className="grid grid-cols-10 gap-2">
                  {icons.map(icon => {
                    const IconComp = ICON_COMPONENTS[icon.id] || Package;
                    return (
                      <button
                        key={icon.id}
                        type="button"
                        onClick={() => setFormData({...formData, icon: icon.id})}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.icon === icon.id 
                            ? 'border-[#00D3A8] bg-[#00D3A8]/10 text-[#00D3A8]' 
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                        title={icon.name}
                      >
                        <IconComp size={20} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de facturation *</label>
                  <select
                    value={formData.billing_period}
                    onChange={(e) => setFormData({...formData, billing_period: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  >
                    {billingPeriods.map(period => (
                      <option key={period.id} value={period.id}>{period.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Duration for one-time */}
              {formData.billing_period === 'one_time' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée de validité (jours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.duration_days || ''}
                    onChange={(e) => setFormData({...formData, duration_days: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Laisser vide pour illimité"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pour un paiement unique, définissez la durée d&apos;accès au produit</p>
                </div>
              )}

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fonctionnalités incluses</label>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Check className="text-[#00D3A8] flex-shrink-0" size={18} />
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        placeholder="Ex: Accès illimité aux AG..."
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-[#00D3A8] text-sm hover:underline"
                  >
                    + Ajouter une fonctionnalité
                  </button>
                </div>
              </div>

              {/* Highlight */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_highlighted}
                    onChange={(e) => setFormData({...formData, is_highlighted: e.target.checked})}
                    className="w-5 h-5 text-[#00D3A8] border-gray-300 rounded focus:ring-[#00D3A8]"
                  />
                  <span className="font-medium text-gray-700">Mettre en avant ce produit</span>
                </label>
                
                {formData.is_highlighted && (
                  <input
                    type="text"
                    value={formData.highlight_text}
                    onChange={(e) => setFormData({...formData, highlight_text: e.target.value})}
                    placeholder="Texte du badge (ex: ⭐ Recommandé, 🔥 Populaire...)"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                )}
              </div>

              {/* Limits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Limite utilisateurs</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_users || ''}
                    onChange={(e) => setFormData({...formData, max_users: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Illimité"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Limite immeubles</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_buildings || ''}
                    onChange={(e) => setFormData({...formData, max_buildings: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Illimité"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]"
                  />
                </div>
              </div>

              {/* Stripe Sync Option */}
              <div className="bg-purple-50 rounded-lg p-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sync_stripe}
                    onChange={(e) => setFormData({...formData, sync_stripe: e.target.checked})}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <span className="font-medium text-purple-800">Synchroniser avec Stripe</span>
                    <p className="text-sm text-purple-600">Créer automatiquement le produit et le prix dans Stripe</p>
                  </div>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] disabled:opacity-50 flex items-center space-x-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      <span>Enregistrement...</span>
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      <span>{showCreateModal ? 'Créer le produit' : 'Enregistrer'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProduits;
